import * as R from "ramda";

import {
  FETCH_MATCH_DETAILS,
  SELECT_CANDIDATE,
  SELECT_CANDIDATES,
  UPDATE_CANDIDATE_STATUS,
  RESET_SELECTED_CANDIDATE,
  FETCH_MATCH_MESSAGES,
  SKIP_SCREENING,
  SEND_SCREENING_REMINDER,
  REQUEST_CV_UPDATE,
  CREATE_MATCH_NOTES,
  UPDATE_MATCH_NOTES,
  LIST_BATCH_ACTION,
  LIST_BATCH_INVITE,
  FETCH_CANDIDATES_WITH_PAGINATION,
  FETCH_CANDIDATES_WITHOUT_PAGINATION,
  FETCH_CANDIDATES_BASE,
  FETCH_CANDIDATE_BY_ID,
  UPDATE_CANDIDATE_BY_ID
} from "../actions/candidates";
import { SELECT_COMPANY } from "../actions/company";
import { UNSET, NEW_MESSAGE } from "../actions/chat";
import {
  BATCH_ACTIONS,
  CANDIDATE_LISTS,
  CANDIDATE_TYPES
} from "../../constants/opportunity";
import { getCandidateScreeningStatus, idInList } from "../../utils/candidates";
import {
  getBatchExternalFails,
  getBatchInternalFails
} from "../../utils/batchActions";

const listInitialStatus = {
  candidates: [],
  loading: true,
  rejected: false
};

const initialMetadata = {
  metadata: {
    total: 0,
    registered: 0,
    external: 0
  }
};

const listsInitialState = {
  [CANDIDATE_LISTS.suggested]: { ...listInitialStatus, ...initialMetadata },
  [CANDIDATE_LISTS.hunting]: { ...listInitialStatus, ...initialMetadata },
  [CANDIDATE_LISTS.selected]: listInitialStatus, // TODO enable metadata when it's available
  [CANDIDATE_LISTS.rejected]: listInitialStatus // TODO enable metadata when it's available
};

const initialState = {
  matched: [],
  selected: null,
  lists: listsInitialState,
  idNoteLoading: null,
  stats: [],
  multipleSelectedCandidates: [],
  candidatesBase: { ...listInitialStatus, ...initialMetadata }
};

const addLoadingFlagToCandidate = (state, candidateId, key, value) => {
  const addLoadingFlag = R.map(
    R.when(R.propEq("id", candidateId), R.assoc(key, value))
  );

  return R.evolve({
    selected: R.when(R.propEq("id", candidateId), R.assoc(key, value)),
    lists: {
      [CANDIDATE_LISTS.suggested]: addLoadingFlag,
      [CANDIDATE_LISTS.hunting]: addLoadingFlag,
      [CANDIDATE_LISTS.selected]: addLoadingFlag,
      [CANDIDATE_LISTS.rejected]: addLoadingFlag
    }
  })(state);
};

export const processCandidateUpdate = ({ payload, state }) => {
  const interestedCandidatesIds = R.pluck("id")([
    ...state.lists[CANDIDATE_LISTS.suggested].candidates,
    ...R.filter(
      R.propEq("candidateLikeOpportunity", true),
      state.lists[CANDIDATE_LISTS.selected].candidates
    ),
    ...R.filter(
      R.propEq("candidateLikeOpportunity", true),
      state.lists[CANDIDATE_LISTS.rejected].candidates
    )
  ]);

  const isRegistered = R.pathOr(true, ["candidate", "isRegistered"], payload);

  const candidateLikeOpportunity = interestedCandidatesIds.includes(
    payload.candidate.id
  );
  const screeningStatus = getCandidateScreeningStatus(
    payload.candidate,
    payload.opportunity
  );
  const wrongScreening = ["pending", "disapproved"].includes(screeningStatus);

  if (payload.explicitMoveToProcess && payload.status === "accepted") {
    return {
      isSelected: false,
      isRejected: false,
      wrongScreening
    };
  }

  const isSelected =
    payload.status === "accepted" &&
    !payload.candidate.isPreviousMatch &&
    (!candidateLikeOpportunity ||
      (candidateLikeOpportunity && wrongScreening)) &&
    isRegistered;

  const isRejected = payload.status === "rejected";

  return {
    isSelected,
    isRejected,
    wrongScreening
  };
};

const candidateListSideEffect = predicate => ({ candidate, ...args }) => ({
  candidates: R.map(
    R.when(R.propEq("id", candidate.id), predicate({ ...args }))
  )
});

const candidateListInteraction = candidateListSideEffect(
  ({ isSelected, isRejected }) =>
    R.assoc(
      "interaction",
      isSelected ? "selected" : isRejected ? "rejected" : "process"
    )
);

const candidates = (state = initialState, { type, payload = {} }) => {
  switch (type) {
    case FETCH_CANDIDATES_WITH_PAGINATION.STARTED:
    case FETCH_CANDIDATES_WITHOUT_PAGINATION.STARTED: {
      return {
        ...state,
        lists: R.evolve(
          {
            [payload.list]: R.mergeLeft(listInitialStatus)
          },
          state.lists
        )
      };
    }

    case FETCH_CANDIDATES_WITH_PAGINATION.RESOLVED:
    case FETCH_CANDIDATES_WITHOUT_PAGINATION.RESOLVED: {
      const { list, response } = payload;
      const transformedResponse = R.evolve(
        {
          candidates: R.when(
            R.always(R.equals(list, CANDIDATE_LISTS.suggested)),
            R.map(
              /* TODO, change when backend is Ready */
              R.assoc("candidateLikeOpportunity", true)
            )
          )
        },
        response
      );

      return {
        ...state,
        lists: R.evolve(
          {
            [list]: R.mergeLeft({
              ...transformedResponse,
              loading: false,
              rejected: false
            })
          },
          state.lists
        )
      };
    }

    case FETCH_CANDIDATES_WITH_PAGINATION.REJECTED:
    case FETCH_CANDIDATES_WITHOUT_PAGINATION.REJECTED: {
      const { list } = payload.payload;
      return {
        ...state,
        lists: R.evolve(
          {
            [list]: R.mergeLeft({
              candidates: [],
              loading: false,
              rejected: true
            })
          },
          state.lists
        )
      };
    }

    case SEND_SCREENING_REMINDER.STARTED: {
      return addLoadingFlagToCandidate(
        state,
        payload,
        "sendScreeningReminderStatus",
        "loading"
      );
    }

    case SEND_SCREENING_REMINDER.RESOLVED: {
      return addLoadingFlagToCandidate(
        state,
        payload,
        "sendScreeningReminderStatus",
        "resolved"
      );
    }

    case SEND_SCREENING_REMINDER.REJECTED: {
      return addLoadingFlagToCandidate(
        state,
        payload,
        "sendScreeningReminderStatus",
        "rejected"
      );
    }

    case REQUEST_CV_UPDATE.STARTED: {
      return addLoadingFlagToCandidate(
        state,
        payload,
        "requestCvUpdateStatus",
        "loading"
      );
    }

    case REQUEST_CV_UPDATE.RESOLVED: {
      return addLoadingFlagToCandidate(
        state,
        payload,
        "requestCvUpdateStatus",
        "resolved"
      );
    }

    case REQUEST_CV_UPDATE.REJECTED: {
      return addLoadingFlagToCandidate(
        state,
        payload.payload,
        "requestCvUpdateStatus",
        "rejected"
      );
    }

    case UPDATE_CANDIDATE_STATUS.STARTED: {
      /*
        There might be cases where a candidate is in the rejected list but never liked the opportunity
        and is flagged as being part of the process anyways (isPreviousMatch: true) from the backend.
        We're currently not handling those cases as they seem to be remnants of a preious feature test
      */

      const { candidate, list } = payload;

      const { isRejected, isSelected } = processCandidateUpdate({
        payload,
        state
      });

      return R.pipe(
        R.evolve({
          lists: {
            [list]: candidateListInteraction({
              candidate,
              isRejected,
              isSelected
            })
          },
          multipleSelectedCandidates: R.reject(
            R.propEq("id", R.prop("id", candidate))
          )
        }),
        // If candidate is NOT a match and is being liked => move to selected list
        // The candidate is NOT a match if it's not interested or
        // the screening is not completed yet
        R.when(
          R.always(isSelected),
          R.evolve({
            lists: {
              [CANDIDATE_LISTS.selected]: {
                candidates: R.compose(
                  R.prepend,
                  R.assoc("changingList", true)
                )(payload.candidate)
              }
            }
          })
        ),
        // If the candidate is being rejected, move to rejected list
        R.when(
          R.always(isRejected),
          R.evolve({
            lists: {
              [CANDIDATE_LISTS.rejected]: {
                candidates: R.compose(
                  R.prepend,
                  R.assoc("changingList", true)
                )(payload.candidate)
              }
            }
          })
        )
      )(state);
    }

    case UPDATE_CANDIDATE_STATUS.RESOLVED: {
      const { isRejected, isSelected } = processCandidateUpdate({
        payload,
        state
      });

      const { candidate, status } = payload;
      // If the person was accepted, it can be in the selected list or in the process, we check just in case.
      const transformer =
        status === "accepted"
          ? {
              [CANDIDATE_LISTS.selected]: {
                candidates: R.compose(
                  R.uniqBy(R.prop("id")),
                  R.map(
                    R.when(
                      R.propEq("id", R.prop("id", candidate)),
                      R.dissoc("changingList")
                    )
                  )
                )
              }
            }
          : {
              [CANDIDATE_LISTS.rejected]: {
                candidates: R.compose(
                  R.uniqBy(R.prop("id")),
                  R.map(
                    R.when(
                      R.propEq("id", R.prop("id", candidate)),
                      R.dissoc("changingList")
                    )
                  )
                )
              }
            };
      return R.evolve(
        {
          lists: {
            [CANDIDATE_LISTS.suggested]: candidateListInteraction({
              candidate,
              isRejected,
              isSelected
            }),
            [CANDIDATE_LISTS.hunting]: candidateListInteraction({
              candidate,
              isRejected,
              isSelected
            }),
            ...transformer
          }
        },
        state
      );
    }

    case UPDATE_CANDIDATE_STATUS.REJECTED: {
      const {
        payload: { candidate, list, status }
      } = payload;
      // If the person was accepted, it will be in the selected list.
      const transformer =
        status === "accepted"
          ? {
              [CANDIDATE_LISTS.selected]: {
                candidates: R.reject(R.propEq("id", R.prop("id", candidate)))
              }
            }
          : {
              [CANDIDATE_LISTS.rejected]: {
                candidates: R.reject(R.propEq("id", R.prop("id", candidate)))
              }
            };
      return R.evolve(
        {
          lists: {
            ...transformer,
            [list]: {
              candidates: R.map(
                R.when(
                  R.propEq("id", R.prop("id", candidate)),
                  R.dissoc("interaction")
                )
              )
            }
          }
        },
        state
      );
    }

    case LIST_BATCH_ACTION.STARTED: {
      // we use the from param to explain where these candidates are from... either "list" or "process"
      const { action, candidates } = payload;

      // If there's candidates moving from the list
      const { list } = payload;

      const transformer = R.equals(BATCH_ACTIONS.list.moveToRejected, action)
        ? {
            // TODO use R.concat
            [CANDIDATE_LISTS.rejected]: {
              candidates: rejected => [
                ...R.map(R.assoc("changingList", true))(candidates),
                ...rejected
              ]
            }
          }
        : R.equals(BATCH_ACTIONS.list.moveToSelected, action)
        ? {
            // TODO use R.concat
            [CANDIDATE_LISTS.selected]: {
              candidates: selected => [
                ...R.map(R.assoc("changingList", true))(candidates),
                ...selected
              ]
            }
          }
        : {};

      return R.evolve(
        {
          lists: {
            ...transformer,
            [list]: R.mergeLeft({
              loading: true
            })
          }
        },
        state
      );
    }

    case LIST_BATCH_ACTION.RESOLVED: {
      // we use the from param to explain where these candidates are from... either "list" or "process"
      const { action, candidates, responses } = payload;

      const [
        externalResponse,
        completedScreeningResponse,
        pendingScreeningResponse
      ] = responses;

      const failedIds = R.flatten([
        getBatchExternalFails(externalResponse),
        getBatchInternalFails(completedScreeningResponse),
        getBatchInternalFails(pendingScreeningResponse)
      ]);

      const candidateIds = R.compose(
        R.map(R.prop("id")),
        R.reject(idInList(failedIds))
      )(candidates);

      const { list } = payload;

      const dissocChangingList = R.compose(
        R.map(R.when(idInList(candidateIds), R.dissoc("changingList"))),
        R.reject(idInList(failedIds))
      );

      const transformer = R.equals(BATCH_ACTIONS.list.moveToRejected, action)
        ? {
            [CANDIDATE_LISTS.rejected]: { candidates: dissocChangingList }
          }
        : R.equals(BATCH_ACTIONS.list.moveToSelected, action)
        ? {
            [CANDIDATE_LISTS.selected]: { candidates: dissocChangingList }
          }
        : {};

      return R.evolve(
        {
          lists: {
            ...transformer,
            [list]: R.mergeLeft({
              loading: false
            })
          }
        },
        state
      );
    }

    case LIST_BATCH_ACTION.REJECTED: {
      const {
        payload: { action, candidates, list }
      } = payload;

      const candidateIds = R.map(R.prop("id"), candidates);

      const transformer = R.equals(BATCH_ACTIONS.list.moveToRejected, action)
        ? {
            [CANDIDATE_LISTS.rejected]: {
              candidates: R.reject(idInList(candidateIds))
            }
          }
        : R.equals(BATCH_ACTIONS.list.moveToSelected, action)
        ? {
            [CANDIDATE_LISTS.selected]: {
              candidates: R.reject(idInList(candidateIds))
            }
          }
        : {};

      return R.evolve(
        {
          lists: {
            ...transformer,
            [list]: R.mergeLeft({
              loading: false
            })
          }
        },
        state
      );
    }

    case LIST_BATCH_INVITE.STARTED: {
      // we use the from param to explain where these candidates are from... either "list" or "process"
      const { candidates } = payload;
      const candidateIds = R.map(R.prop("id"), candidates);

      // If there's candidates moving from the list
      const { list } = payload;

      return R.evolve(
        {
          lists: {
            [list]: {
              candidates: R.map(
                R.when(
                  idInList(candidateIds),
                  R.assoc("inviteStatus", "loading")
                )
              )
            }
          }
        },
        state
      );
    }

    case LIST_BATCH_INVITE.RESOLVED: {
      // we use the from param to explain where these candidates are from... either "list" or "process"
      const { candidates, type, response } = payload;

      const failedIds = R.equals(type, CANDIDATE_TYPES.external.type)
        ? getBatchExternalFails(response)
        : getBatchInternalFails(response);

      const candidateIds = R.compose(
        R.map(R.prop("id")),
        R.reject(idInList(failedIds))
      )(candidates);

      const { list } = payload;

      return R.evolve(
        {
          lists: {
            [list]: {
              candidates: R.map(
                R.compose(
                  R.when(
                    idInList(candidateIds),
                    R.assoc("inviteStatus", "resolved")
                  ),
                  R.when(idInList(failedIds), R.dissoc("inviteStatus"))
                )
              )
            }
          }
        },
        state
      );
    }

    case LIST_BATCH_INVITE.REJECTED: {
      const {
        payload: { candidates, list }
      } = payload;

      const candidateIds = R.map(R.prop("id"), candidates);

      return R.evolve(
        {
          lists: {
            [list]: {
              candidates: R.map(
                R.when(idInList(candidateIds), R.dissoc("inviteStatus"))
              )
            }
          }
        },
        state
      );
    }

    case SKIP_SCREENING.STARTED: {
      const { candidate, list } = payload;
      return R.evolve({
        lists: {
          [list]: candidateListInteraction({ candidate })
        }
      })(state);
    }

    case SKIP_SCREENING.REJECTED: {
      const { candidate, list } = payload;
      return R.evolve({
        lists: {
          [list]: candidateListSideEffect(R.dissoc("interaction"), {
            candidate
          })
        }
      })(state);
    }

    case "candidates/MULTIPLE_SELECT": {
      const { candidate, selected } = payload;
      return R.evolve(
        {
          multipleSelectedCandidates: selected
            ? R.append(candidate)
            : R.reject(R.propEq("id", R.path(["id"], candidate)))
        },
        state
      );
    }

    case "candidates/RESET_MULTIPLE_SELECT": {
      return R.evolve(
        {
          multipleSelectedCandidates: R.always([])
        },
        state
      );
    }

    case FETCH_MATCH_DETAILS.RESOLVED:
      return {
        ...state,
        selected: { ...payload, ...state.selected }
      };

    case FETCH_MATCH_MESSAGES.RESOLVED:
      return {
        ...state,
        selected: { ...state.selected, messages: payload }
      };

    case NEW_MESSAGE.TYPE: {
      const isMessageForActiveChat =
        state.selected && state.selected.id === payload.idCandidate;

      return isMessageForActiveChat
        ? R.evolve({ selected: { messages: R.append(payload) } })(state)
        : state;
    }
    case CREATE_MATCH_NOTES.STARTED:
    case UPDATE_MATCH_NOTES.STARTED:
      return {
        ...state,
        idNoteLoading: payload.id
      };

    case CREATE_MATCH_NOTES.RESOLVED:
      return {
        ...state,
        selected: {
          ...state.selected,
          notes: R.append(payload, state.selected.notes)
        },
        idNoteLoading: null
      };

    case UPDATE_MATCH_NOTES.RESOLVED:
      return {
        ...state,
        selected: {
          ...state.selected,
          notes: R.map(
            note => (note.id === payload.id ? payload : note),
            state.selected.notes
          )
        },
        idNoteLoading: null
      };

    case CREATE_MATCH_NOTES.REJECTED:
    case UPDATE_MATCH_NOTES.REJECTED:
      return {
        ...state,
        idNoteLoading: null
      };
    case FETCH_CANDIDATE_BY_ID.RESOLVED:
    case SELECT_CANDIDATE.TYPE:
      return {
        ...state,
        selected: payload
      };
    case UPDATE_CANDIDATE_BY_ID.RESOLVED:
      return {
        ...state,
        selected: { ...state.selected, ...payload }
      };
    case SELECT_CANDIDATES.TYPE: {
      const { isChecked, candidates } = payload;

      return {
        ...state,
        multipleSelectedCandidates: isChecked ? candidates : []
      };
    }

    case UNSET.TYPE:
      return {
        ...state,
        selected: null
      };

    case RESET_SELECTED_CANDIDATE.TYPE:
      return {
        ...state,
        selected: null
      };

    case SELECT_COMPANY.TYPE:
      return initialState;

    case FETCH_CANDIDATES_BASE.REJECTED:
      return R.evolve(
        {
          candidatesBase: R.mergeLeft({ candidates: [], loading: false })
        },
        state
      );

    case FETCH_CANDIDATES_BASE.RESOLVED:
      return R.evolve(
        {
          candidatesBase: R.mergeLeft({ ...payload, loading: false })
        },
        state
      );
    case UPDATE_CANDIDATE_BY_ID.REJECTED:
      return {
        ...state,
        selected: state.selected
      };
    default:
      return state;
  }
};

export default candidates;
